import { API_ENDPOINTS } from '../../config/apiConfig';
import { POST_TYPES, PostTypeKey } from '../../types/common.type';
import {
  ItemListProduct,
  detailPostInterface
} from '../../types/post.type';
import apiClient from './apiClient';
import csrfTokenApi from './csrf_create';

/**
 * Interface for Post API service
 */
export interface PostApiService {
   fetchPostDetails(id: string, language: string, type?: string): Promise<detailPostInterface>;
  fetchPostDetailsBySlug(slug: string, type?: string): Promise<detailPostInterface>;
  fetchPostsByCategory(category: string, language: string, type?: string): Promise<ItemListProduct[]>;
  fetchLatestPosts(language: string, type?: string): Promise<ItemListProduct[]>;
  searchPosts(query: string, language: string, type?: string): Promise<ItemListProduct[]>;
  trackPostView(type: string, postId: number |string): Promise<any>;
  ratingPostApi(id: number, type: PostTypeKey, params: RatingInterface): Promise<any>;
  likePostApi(id: number, type: PostTypeKey, params: ParamLikeMovie): Promise<any>;
}

/**
 * Interface for rating parameters
 */
interface RatingInterface {
  rating?: number;
  content?: string;
}

/**
 * Interface for like parameters
 */
interface ParamLikeMovie {
  like: 1 | 2; // 1: like, 2: dislike
}

/**
 * Post API service implementation
 * Contains methods to fetch various post data
 */
export const postApi: PostApiService = {
  /**
   * Fetch popular posts by type
   * @param language Current language
   * @param type Post type (movie, game, comic, novel)
   * @returns Promise with list of popular posts
   */
  /**
   * Fetch post details by ID and type
   * @param id Post ID or slug
   * @param language Current language
   * @param type Post type (movie, game, comic, novel)
   * @returns Promise with post details
   */
  fetchPostDetails: async (
    id: string,
    language: string, 
    type: string = POST_TYPES.MOVIES
  ): Promise<detailPostInterface> => {
    try {
      const response = await apiClient.get<{data: detailPostInterface}>(
        `/${language}${API_ENDPOINTS.DETAIL}${type}/${id}/`
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching ${type} details:`, error);
      throw error;
    }
  },

  fetchPostDetailsBySlug: async (
    slug: string, 
    type: string = POST_TYPES.MOVIES,
  ): Promise<detailPostInterface> => {
    try {
      const response = await apiClient.get<{data: detailPostInterface}>(
        `${API_ENDPOINTS.DETAIL}${type}/${slug}/`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Not Found');
      }
      console.error(`Error fetching ${type} details:`, error);
      throw error;
    }
  },

  /**
   * Fetch posts by category
   * @param category Category slug
   * @param type Post type (movie, game, comic, novel)
   * @param language Current language
   * @returns Promise with list of posts in category
   */
  fetchPostsByCategory: async (
    category: string,
    type: string = POST_TYPES.MOVIES,
    language: string
  ): Promise<ItemListProduct[]> => {
    try {
      const response = await apiClient.get<{data: ItemListProduct[]}>(
        `/${language}${API_ENDPOINTS.CATEGORY}${type}/${category}`
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching ${type} by category:`, error);
      throw error;
    }
  },

  /**
   * Fetch latest posts by type
   * @param type Post type (movie, game, comic, novel)
   * @param language Current language
   * @returns Promise with list of latest posts
   */
  fetchLatestPosts: async (
    type: string = POST_TYPES.MOVIES,
    language: string
  ): Promise<ItemListProduct[]> => {
    try {
      const response = await apiClient.get<{data: ItemListProduct[]}>(
        `/${language}${API_ENDPOINTS.LIST}${type}/latest`
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching latest ${type}:`, error);
      throw error;
    }
  },

  /**
   * Search posts by type and query
   * @param query Search term
   * @param type Post type (movie, game, comic, novel)
   * @param language Current language
   * @returns Promise with list of search results
   */
  searchPosts: async (
    query: string,
    type: string = POST_TYPES.MOVIES,
    language: string
  ): Promise<ItemListProduct[]> => {
    try {
      const response = await apiClient.get<{data: ItemListProduct[]}>(
        `/${language}${API_ENDPOINTS.SEARCH}${type}?q=${encodeURIComponent(query)}`
      );
      return response.data.data;
    } catch (error) {
      console.error(`Error searching ${type}:`, error);
      throw error;
    }
  },
  
  /**
   * Track a view for a post
   * @param type Post type (movie, comic, novel, etc.)
   * @param postId ID of the post to track the view for
   * @returns Promise with tracking response
   */
  trackPostView: async (type: string, postId: number|string): Promise<any> => {
    try {
      // First get a CSRF token
      const csrfToken = await csrfTokenApi.getCSRFToken();
      
      // Create FormData and append CSRF token
      const formData = new FormData();
      formData.append('csrf_token', csrfToken);
      
      // Make the API request to track the view
      const response = await apiClient.post(
        `/posts/action/views/${type}/${postId}/`,
        formData
      );
      
      return response;
    } catch (error) {
      console.error(`Error tracking view for ${type} post ${postId}:`, error);
      throw error;
    }
  },

  /**
   * Rate a post
   * @param id Post ID
   * @param type Post type (movie, game, comic, novel)
   * @param params Rating parameters
   * @returns Promise with rating response
   */
  ratingPostApi: async (
    id: number,
    type: string,
    params: RatingInterface
  ): Promise<any> => {
    try {
      const csrfToken = await csrfTokenApi.getCSRFToken();
      const uri = `${API_ENDPOINTS.RATING}${type}/${id}/`;
      const formData = new FormData();
      formData.append('rating', params.rating?.toString() || '');
      formData.append('content', params.content || '');
      formData.append('csrf_token', csrfToken);

      return apiClient.post(uri, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (error) {
      console.error(`Error rating ${type} post ${id}:`, error);
      throw error;
    }
  },

  /**
   * Like or dislike a post
   * @param id Post ID
   * @param type Post type (movie, game, comic, novel)
   * @param params Like parameters
   * @returns Promise with like response
   */
  likePostApi: async (
    id: number,
    type: string,
    params: ParamLikeMovie
  ): Promise<any> => {
    try {
      const csrfToken = await csrfTokenApi.getCSRFToken();
      const uri = `${API_ENDPOINTS.LIKE}${type}/${id}`;
      const formData = new FormData();
      formData.append('like', params.like.toString());
      formData.append('csrf_token', csrfToken);

      return apiClient.post(uri, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (error) {
      console.error(`Error liking ${type} post ${id}:`, error);
      throw error;
    }
  },
};
